import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { ArrowLeft, Check, UploadExportSmall, X } from "@wix/wix-ui-icons-common/lazy";
import {
  Box,
  Card,
  Table,
  TableToolbar,
  Button,
  Loader,
  Text,
  Divider,
  SectionHelper,
  Dropdown,
  Search,
} from "@wix/design-system";

interface Member {
  id: string;
  name: string;
  email: string;
  hasAvatar: boolean;
  avatarUrl: string;
}

interface MembersListProps {
  onBack: () => void;
}

const MembersList: React.FC<MembersListProps> = ({ onBack }) => {
  const { t } = useTranslation();
  const [members, setMembers] = useState<Member[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [updateResults, setUpdateResults] = useState<{
    success: string[];
    failed: { memberId: string; error: string }[];
  } | null>(null);
  const [filter, setFilter] = useState({
    avatarStatus: { id: '0', value: 'All Members' },
    name: '',
  });

  const API_BASE_URL = "/api";

  useEffect(() => {
    fetchMembers();
  }, []);

  // Filter members based on filter state
  useEffect(() => {
    let filtered = members;

    // Filter by avatar status
    if (filter.avatarStatus.value === 'Has Avatar') {
      filtered = filtered.filter((member) => member.hasAvatar);
    } else if (filter.avatarStatus.value === 'No Avatar') {
      filtered = filtered.filter((member) => !member.hasAvatar);
    }

    // Filter by name/email search
    if (filter.name) {
      const searchTerm = filter.name.toLowerCase();
      filtered = filtered.filter(
        (member) =>
          member.name.toLowerCase().includes(searchTerm) ||
          member.email.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredMembers(filtered);
  }, [members, filter]);

  const fetchMembers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const instance = new URLSearchParams(window.location.search).get("instance");
      const url = instance
        ? `${API_BASE_URL}/members?instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/members`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setMembers(data.members || []);
      setIsLoading(false);
    } catch (err: any) {
      console.error("Error fetching members:", err);
      setError(err.message || t("ml-fail-fetch"));
      setIsLoading(false);
    }
  };

  const handleBulkUpdate = async (idsToUpdate?: string[]) => {
    const memberIds = idsToUpdate || selectedIds;
    if (memberIds.length === 0) {
      setError(t("ml-select-one"));
      return;
    }

    try {
      setIsUpdating(true);
      setError(null);
      setUpdateResults(null);

      const instance = new URLSearchParams(window.location.search).get("instance");
      const url = instance
        ? `${API_BASE_URL}/bulk-update-avatars?instance=${encodeURIComponent(instance)}`
        : `${API_BASE_URL}/bulk-update-avatars`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ memberIds }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const results = await response.json();
      setUpdateResults(results);
      
      // Refresh the members list
      await fetchMembers();
      
      // Clear selection
      setSelectedIds([]);
    } catch (err: any) {
      console.error("Error updating avatars:", err);
      setError(err.message || t("ml-fail-update"));
    } finally {
      setIsUpdating(false);
    }
  };

  const columns = [
    { title: t("ml-name"), render: (row: Member) => row.name },
    { title: t("ml-email"), render: (row: Member) => row.email },
    {
      title: t("ml-avatar-status"),
      render: (row: Member) => (
        <Box verticalAlign="middle">
          {row.hasAvatar ? (
            <Check size="24px" />
          ) : (
            <X size="24px" />
          )}
        </Box>
      ),
    },
  ];

  const MainToolbar = () => {
    return (
      <TableToolbar>
        <TableToolbar.ItemGroup position="start">
          <TableToolbar.Item>
            <TableToolbar.Label>
              {filteredMembers.length} {filteredMembers.length === 1 ? 'member' : 'members'}
              {filteredMembers.length !== members.length && ` of ${members.length}`}
            </TableToolbar.Label>
          </TableToolbar.Item>
          <TableToolbar.Item>
            <Dropdown
              options={[
                { id: '0', value: 'All Members' },
                { id: '1', value: 'Has Avatar' },
                { id: '2', value: 'No Avatar' },
              ]}
              selectedId={filter.avatarStatus.id}
              border="round"
              size="small"
              popoverProps={{ appendTo: 'window' }}
              onSelect={(avatarStatus: any) => setFilter({ ...filter, avatarStatus })}
              valueParser={({ value }) => value}
            />
          </TableToolbar.Item>
        </TableToolbar.ItemGroup>
        <TableToolbar.ItemGroup position="end">
          <TableToolbar.Item>
            <Search
              size="small"
              onChange={(event) =>
                setFilter({ ...filter, name: event.target.value })
              }
              onClear={() => setFilter({ ...filter, name: '' })}
            />
          </TableToolbar.Item>
          <TableToolbar.Item>
            <Button
              skin="inverted"
              size="small"
              prefixIcon={<ArrowLeft />}
              onClick={onBack}
            >{t("ml-back")}</Button>
          </TableToolbar.Item>
        </TableToolbar.ItemGroup>
      </TableToolbar>
    );
  };

  const ActionsToolbar = ({ selectedCount, getSelectedIds, clearSelection }: any) => {
    const handleSelectAll = () => {
      const allIds = filteredMembers.map((m) => m.id);
      const currentSelectedIds = getSelectedIds ? getSelectedIds() : selectedIds;
      if (currentSelectedIds.length === allIds.length) {
        setSelectedIds([]);
        if (clearSelection) {
          clearSelection();
        }
      } else {
        setSelectedIds(allIds);
      }
    };

    const handleBulkUpdateClick = async () => {
      const idsToUpdate = getSelectedIds ? getSelectedIds() : selectedIds;
      if (idsToUpdate.length === 0) {
        setError(t("ml-select-one"));
        return;
      }
      await handleBulkUpdate(idsToUpdate);
    };

    return (
      <TableToolbar>
        <TableToolbar.ItemGroup position="start">
          <TableToolbar.Item>
            <TableToolbar.Label>{t("ml-selected", { count: selectedCount })}</TableToolbar.Label>
          </TableToolbar.Item>
          <TableToolbar.Item>
            <Box height="18px">
              <Divider direction="vertical" />
            </Box>
          </TableToolbar.Item>
          <TableToolbar.Item layout="button">
            <Button
              skin="inverted"
              size="small"
              onClick={handleSelectAll}
              disabled={isUpdating}
            >
              {(getSelectedIds ? getSelectedIds() : selectedIds).length === filteredMembers.length
                ? t("ml-deselect-all")
                : t("ml-select-all")}
            </Button>
          </TableToolbar.Item>
          <TableToolbar.Item layout="button">
            <Button
              size="small"
              prefixIcon={<UploadExportSmall />}
              onClick={handleBulkUpdateClick}
              disabled={isUpdating || selectedCount === 0}
            >
              {isUpdating ? t("ml-updating") : t("ml-update-avatars")}
            </Button>
          </TableToolbar.Item>
        </TableToolbar.ItemGroup>
        <TableToolbar.ItemGroup position="end">
          <TableToolbar.Item>
            <Button
              skin="inverted"
              size="small"
              prefixIcon={<ArrowLeft />}
              onClick={onBack}
              disabled={isUpdating}
            >{t("ml-back")}</Button>
          </TableToolbar.Item>
        </TableToolbar.ItemGroup>
      </TableToolbar>
    );
  };

  if (isLoading) {
    return (
      <Card>
        <Box padding="SP6" direction="vertical" verticalAlign="middle" gap="SP2">
          <Loader text={t("ml-loading")} />
        </Box>
      </Card>
    );
  }

  return (
    <Box direction="vertical" gap="SP3">
      {error && (
        <SectionHelper
          title={t("ml-error")}
          skin="danger"
          onClose={() => setError(null)}
        >
          <Text size="small">{error}</Text>
        </SectionHelper>
      )}

      {updateResults && (
        <SectionHelper
          title={t("ml-update-results")}
          skin={
            updateResults.failed.length === 0 ? "success" : "warning"
          }
          onClose={() => setUpdateResults(null)}
        >
          <Box direction="vertical" gap="SP1">
            <Text size="small">
              Successfully updated: {updateResults.success.length} members
            </Text>
            {updateResults.failed.length > 0 && (
              <Text size="small">
                Failed: {updateResults.failed.length} members
              </Text>
            )}
          </Box>
        </SectionHelper>
      )}

      <Card hideOverflow>
        <Table
          data={filteredMembers}
          columns={columns}
          showSelection
          selectedIds={selectedIds}
          onSelectionChanged={(ids: any) => setSelectedIds(ids)}
        >
          <Table.ToolbarContainer>
            {(selectionContext: any) =>
              selectionContext.selectedCount === 0
                ? MainToolbar()
                : ActionsToolbar({ ...selectionContext })
            }
          </Table.ToolbarContainer>
          <Table.Content />
        </Table>
      </Card>
    </Box>
  );
};

export default MembersList;

